<script setup lang="ts">
import { useLocalStorage } from "@vueuse/core";
import VText from "@magnit/core/src/components/VText/VText.vue";
import VChips from "@magnit/core/src/components/VChips/VChips.vue";
import VChipsGroup from "@magnit/core/src/components/VChips/VChipsGroup.vue";
import VIcon from "@magnit/icons/src/components/VIcon/VIcon.vue";
import { clearSearchHistory } from "@magnit/unit-header/src/utils/searchHistory";
import { APP_LOCAL_STORAGE_KEYS } from "~/constants/app";

const props = defineProps<{
  isOpenSuggest?: boolean;
  searchValue: string;
}>();
const emit = defineEmits<{
  "click:suggest": [value: string];
}>();

const { isAuthorized } = useAuth();

const initialDisplayCount = 4;
const isShowFullHistory = ref(false);
const searchHistory = useLocalStorage<string[]>(
  APP_LOCAL_STORAGE_KEYS.searchHistory,
  [],
);
// Dirty hack чтоб юзать чипс как баттон (сброс выбранного состояния после нажатия)
const keyRenderCounter = ref(0);

const displayItems = computed(() => {
  // вернет либо соответствующие поисковому запросу, либо
  if (props.searchValue.length) {
    return searchHistory.value.filter((el) => el.includes(props.searchValue));
  }
  // либо просто историю (короткую или полную)
  return isShowFullHistory.value
    ? searchHistory.value
    : searchHistory.value.slice(0, initialDisplayCount);
});
const countHiddenSuggests = computed(() =>
  Math.max(searchHistory.value.length - initialDisplayCount, 0),
);
// отобразить если включен тоггл, не введено поисковое значение и есть что показать из скрытых
const showMore = computed(
  () =>
    !isShowFullHistory.value &&
    !props.searchValue.length &&
    countHiddenSuggests.value,
);

const updateShowFullHistory = (val: boolean) => (isShowFullHistory.value = val);

const onClickSuggest = (searchValue: string) => {
  emit("click:suggest", searchValue);
  // таймаут чтоб чипс успел покраситься при нажатии
  setTimeout(() => keyRenderCounter.value++, 250);
};

const onClickClear = () =>
  clearSearchHistory(APP_LOCAL_STORAGE_KEYS.searchHistory);

watch(
  () => props.isOpenSuggest,
  (next) => {
    if (next) return;
    updateShowFullHistory(false);
  },
);

watch(isAuthorized, (next) => {
  if (next) return;
  clearSearchHistory(APP_LOCAL_STORAGE_KEYS.searchHistory);
});
</script>

<template>
  <section v-if="searchHistory.length" class="suggestions-history">
    <div v-if="displayItems.length" class="suggestions-history__header">
      <VText
        tag="div"
        font="title-medium"
        class="suggestions-history__header-title"
      >
        Вы искали
      </VText>
      <VIcon
        name="24-outline-trash"
        class="suggestions-history__header-icon"
        @click="onClickClear"
      />
    </div>
    <VChipsGroup wrapped class="suggestions-history__list">
      <VChips
        v-for="item in displayItems"
        :key="`${item}${keyRenderCounter}`"
        :model-value="null"
        type="radio"
        @click.stop="onClickSuggest(item)"
      >
        {{ item }}
      </VChips>
      <VChips
        v-if="showMore"
        :model-value="null"
        type="radio"
        @click.stop="updateShowFullHistory(true)"
      >
        <template #icon>
          <VIcon name="20-outline-down-corner" />
        </template>
        ещё {{ countHiddenSuggests }}
      </VChips>
    </VChipsGroup>
  </section>
</template>

<style scoped lang="postcss">
.suggestions-history {
  &__header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding-top: var(--pl-unit-x2);
    padding-bottom: var(--pl-unit-x1);
  }

  &__header-title {
    display: flex;
    justify-content: space-between;
    gap: var(--pl-unit-x2);
  }

  &__header-icon {
    &:hover {
      opacity: 0.8;
      cursor: pointer;
    }
  }

  &__list {
    padding: var(--pl-unit-x2) 0;
  }
}
</style>
